import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { findCaseStudy, BUSINESS, type CaseStudyResult } from "@/lib/site-data";
import { AnswerBlock } from "@/components/AnswerBlock";

const BASE_URL = "https://lehitradessaeo.com";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: ({ params }) => {
    const study = findCaseStudy(params.slug);
    if (!study) throw notFound();
    return { study };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Case study not found" }] };
    const c = loaderData.study;
    const url = `${BASE_URL}/case-studies/${params.slug}`;
    const title = `${c.title} | ${BUSINESS.shortName}`;
    const description = c.summary.slice(0, 158);
    const ldGraph: Array<Record<string, unknown>> = [
      {
        "@type": "Article",
        headline: c.title,
        description,
        url,
        datePublished: c.publishedAt,
        dateModified: c.publishedAt,
        author: { "@id": `${BASE_URL}/#organization` },
        publisher: { "@id": `${BASE_URL}/#organization` },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        about: { "@type": "Thing", name: `${c.trade} marketing case study` },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["[data-speakable=\"question\"]", "[data-speakable=\"answer\"]"],
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Case Studies", item: `${BASE_URL}/case-studies` },
          { "@type": "ListItem", position: 3, name: c.title, item: url },
        ],
      },
    ];
    if (c.videoUrl) {
      ldGraph.push({
        "@type": "VideoObject",
        name: `${c.title} — client testimonial`,
        description: c.summary,
        embedUrl: c.videoUrl,
        uploadDate: c.publishedAt,
      });
    }
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({ "@context": "https://schema.org", "@graph": ldGraph }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Case study not found</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/case-studies" className="underline">See all case studies</Link>
      </p>
    </main>
  ),
  component: CaseStudyPage,
});

function CaseStudyPage() {
  const { study: c } = Route.useLoaderData();
  return (
    <main className="mx-auto max-w-3xl px-6 py-20">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        {c.trade} · {c.city} · {c.timeline}
      </p>
      <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">{c.title}</h1>
      <p className="mt-5 text-lg text-muted-foreground">{c.summary}</p>

      <AnswerBlock
        question={`What did ${BUSINESS.shortName} actually do for this ${c.trade.toLowerCase()} client?`}
        answer={c.summary}
        bullets={c.approach.slice(0, 3)}
      />

      {c.videoUrl ? (
        <section className="mt-10">
          <div className="aspect-video w-full overflow-hidden rounded-2xl border border-border bg-card">
            <iframe
              src={c.videoUrl}
              title={`${c.title} — client testimonial`}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="h-full w-full"
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Video testimonial from the client.</p>
        </section>
      ) : null}

      <section className="mt-12">
        <h2 className="font-display text-2xl tracking-tight">The challenge</h2>
        <p className="mt-3 leading-relaxed text-muted-foreground">{c.challenge}</p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl tracking-tight">What we did</h2>
        <ul className="mt-4 space-y-2">
          {c.approach.map((step: string) => (
            <li key={step} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm">
              <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{step}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl tracking-tight">Results</h2>
        <div className="mt-4 overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left text-xs uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Metric</th>
                <th className="px-4 py-3">Before</th>
                <th className="px-4 py-3">After</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {c.results.map((r: CaseStudyResult) => (
                <tr key={r.metric}>
                  <td className="px-4 py-3 align-top font-medium text-foreground">
                    {r.metric}
                    <p className="mt-1 text-xs font-normal text-muted-foreground">{r.detail}</p>
                  </td>
                  <td className="px-4 py-3 align-top text-muted-foreground">{r.before}</td>
                  <td className="px-4 py-3 align-top font-semibold text-primary">{r.after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {c.pullQuote && (
        <blockquote className="mt-10 rounded-2xl border-l-4 border-primary bg-card p-6">
          <p className="font-display text-xl leading-snug tracking-tight">
            "{c.pullQuote.quote}"
          </p>
          <p className="mt-3 text-sm text-muted-foreground">— {c.pullQuote.attribution}</p>
        </blockquote>
      )}

      {c.isExample && (
        <p className="mt-8 rounded-xl border border-dashed border-border bg-secondary/30 p-4 text-xs text-muted-foreground">
          Example case study based on a representative client engagement.
          Replace this entry in <code>src/lib/site-data.ts</code> with a real
          client breakdown before publishing.
        </p>
      )}

      <div className="mt-16 rounded-2xl border border-border bg-card p-8 text-center">
        <h3 className="font-display text-2xl tracking-tight">Want results like these?</h3>
        <p className="mt-3 text-muted-foreground">
          Free local SEO audit. No pitch deck, no contract pressure.
        </p>
        <Link
          to="/contact"
          className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Get my free audit
        </Link>
      </div>
    </main>
  );
}